// src/components/PromoFormOnlyCrypto.jsx

import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCrystals, setSelectedId } from '../../../store/slices/crystalsSlice';
import { setBudget, setSelectedBlockchain, setFoundToken, clearFoundToken, setPromoterWalletAddress } from '../../../store/slices/budgetSlice';
import { addCustomDay, selectDay, selectAllDays, selectSelectedDay, removeCustomDay, clearSelectedDay } from '../../../store/slices/promoTimeSlice';
import { clearEditMode, setSelectedPromo } from '../../../store/slices/selectedPromoSlice';
import { setPage, goBack } from '../../../store/slices/pageSlice';
import { usePromoLeaveGuard } from '../../../context/PromoLeaveGuardContext';
import { clearPromoCreationState, clearPromoFormFields } from '../../../utils/promoFormDraft';
import { setPaymentType } from '../../../store/slices/paymentSlice';
import { setPromoterData } from '../../../store/slices/promoTelegramSlice';
import { setLink as setFormLink, setParticipantsCount, setPrizePlacesCount, setMaxParticipantsCount, setSelectedCrystalCategory } from '../../../store/slices/promoFormSlice';
import { setTasks } from '../../../store/slices/TasksSlice';
import TaskApi from '../../../api/taskApi';
import { distributeWinnersByPercent, distributeBudgetByPercent, isMatchingPlaceTotals, resolveWinPercents, resolveBudgetPercents, distributePercentsFromCounts } from '../../../utils/placeWinners';
import { findCrystalCategoryForPromo } from '../../../utils/participantLimits';

import PromoForm from '../PromoForm';
import FormAccardion from '../items/FormAccardion';
import CoinBadge from '../../coin_badge/CoinBadge';
import TasksList from '../items/TasksList';
import getIcon from '../../../utils/Icon';
import tokenApi from "../../../api/tokenApi";
import BufferHandler from '../../../utils/bufferHandler';
import PromosApi from "../../../api/PromosApi";
import blockchainApi from "../../../api/blockchainApi";
import Header from '../../header/Header';
import { useKeyboard } from '../../Keyboard';
import SettingsIcon from '../../icons/SettingsIcon';
import { PromoGuideProvider, usePromoGuide, useAdvanceGuideOnKeyboardClose, GuideSkipButton, PromoTypeIntroBanner } from '../PromoGuideContext';
import PromoGuideTour from '../PromoGuideTour';
import { hardhatChainOption } from '../../../utils/featureFlags';

const API_BASE_URL = 'https://api.duotetra.quest';

const Snackbar = React.memo(function SnackbarInner({ message, type, isVisible, onClose, isAnimated, tokenData, blockchainName, isBlockchainSwitched }) {
    if (!isVisible) return null;

    const bgColor = type === 'hint'
        ? 'bg-[#232323]'
        : type === 'error'
            ? 'bg-[#fb2c36]'
            : 'bg-[#00c950]';
    const borderColor = type === 'hint'
        ? 'border-transparent'
        : type === 'error'
            ? 'border-red-500'
            : 'border-green-500';

    return (
        <div className={`fixed top-0 left-1/2 transform -translate-x-1/2 z-50 
            ${bgColor} ${borderColor} border text-white px-4 pt-24 pb-3 shadow-xl 
            transition-all duration-500 ease-out min-w-[100%]
            ${isAnimated ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-full opacity-0'}
        `}>
            <div className="flex items-center justify-between">
                <div className="flex items-center justify-center space-x-2 flex-1">
                    {type !== 'hint' && (
                        <div className={`w-1.5 h-1.5 rounded-full ${type === 'error' ? 'bg-red-300' : 'bg-green-300'}`} />
                    )}

                    {type === 'success' && tokenData ? (
                        <div className="flex items-center space-x-2">
                            <img
                                src={`${API_BASE_URL}/${tokenData.logoPath}`}
                                alt={tokenData.symbol}
                                className="w-5 h-5 rounded-full object-cover"
                            />

                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-white">{message}</span>
                                <div className="flex items-center space-x-1 mt-0.5">
                                    <span className="text-[10px] text-gray-300">{tokenData.symbol}</span>
                                    <span className="text-[10px] text-gray-400">•</span>
                                    <span className="text-[10px] text-gray-300">{blockchainName}</span>
                                    {isBlockchainSwitched && (
                                        <>
                                            <span className="text-[10px] text-gray-400">•</span>
                                            <span className="text-[10px] text-yellow-300">🔄 Авто</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <span className="text-xs font-medium text-white text-center">{message}</span>
                    )}
                </div>

                <button
                    onClick={onClose}
                    className="text-white/70 hover:text-white transition-colors duration-200 flex-shrink-0 ml-3"
                    aria-label="Close notification"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {type !== 'hint' && (
                <div className="absolute bottom-0 left-0 h-0.5 bg-white/20 overflow-hidden w-full">
                    <div className={`h-full ${type === 'error' ? 'bg-red-300' : 'bg-green-300'} transition-all duration-4000 ease-linear`} />
                </div>
            )}
        </div>
    );
});

const CUSTOM_DAY_MIN = 1;
const CUSTOM_DAY_MAX = 27;

const PromoTime = () => {
    const dispatch = useDispatch();
    const { openKeyboard } = useKeyboard();
    const allDays = useSelector(selectAllDays);
    const selectedDay = useSelector(selectSelectedDay);
    const editingDayRef = React.useRef(null);
    const { step, completePromoTime } = usePromoGuide();
    const showPromoTimeGuide = step === 'promoTime';
    useAdvanceGuideOnKeyboardClose(showPromoTimeGuide, completePromoTime);

    const applyCustomDay = React.useCallback((rawValue) => {
        const editingDay = editingDayRef.current;
        editingDayRef.current = null;

        const normalized = String(rawValue).trim().replace(',', '.');
        if (!normalized) return;

        const parsed = parseFloat(normalized);
        if (isNaN(parsed)) return;

        const day = Math.min(
            Math.max(Math.floor(parsed), CUSTOM_DAY_MIN),
            CUSTOM_DAY_MAX
        );

        if (editingDay) {
            dispatch(removeCustomDay(editingDay));
            dispatch(addCustomDay(day));
            if (selectedDay === editingDay) {
                dispatch(selectDay(day));
            }
        } else {
            dispatch(addCustomDay(day));
            dispatch(selectDay(day));
        }
    }, [dispatch, selectedDay]);

    const openCustomDayKeyboard = React.useCallback((initialDay = null) => {
        editingDayRef.current = initialDay;
        const initial = initialDay != null ? String(initialDay) : '';

        openKeyboard({
            type: 'numeric',
            value: initial,
            label: `Promo days (${CUSTOM_DAY_MIN}-${CUSTOM_DAY_MAX})`,
            liveUpdate: false,
            onChange: applyCustomDay,
        });
    }, [openKeyboard, applyCustomDay]);

    const handleCustomDayClick = () => {
        openCustomDayKeyboard(null);
    };

    const handleDayClick = (day) => {
        dispatch(selectDay(day));
    };

    const handleDayEdit = (e, day) => {
        e.stopPropagation();
        if (e.ctrlKey || e.metaKey) {
            openCustomDayKeyboard(day);
        }
    };

    const standardDays = [1, 2, 3, 4, 5, 7, 10, 15, 27];

    return (
        <div
            data-guide="promoTime"
            className="w-full flex flex-col items-center pb-4"
        >
            <div className='w-full flex flex-row items-center justify-center text-[10px] text-[#95959] mb-2'>
                chose promo time period
            </div>

            <div className='w-full flex flex-row items-center justify-center flex-wrap gap-2'>
                {standardDays.map((value, i) => (
                    <div key={value} className='flex flex-col items-center'>
                        <div className='w-full flex flex-row items-center justify-center text-[#95959] text-[7px] h-4'>
                            {i === 0 && 'min'}
                            {i === standardDays.length - 1 && 'max'}
                        </div>

                        <div
                            onClick={() => handleDayClick(value)}
                            className={`w-[58px] xs:w-[54px] xs:h-[27px] h-[27px] rounded-[4px] border flex flex-row items-center justify-center text-[11px] cursor-pointer transition-all duration-200 ${
                                selectedDay === value
                                    ? 'bg-[#353232] border-white/30 text-white'
                                    : 'bg-[#121212] border-[#353232] text-[#959595] hover:bg-[#1a1a1a] hover:border-white/20'
                            }`}
                        >
                            {value} {value === 1 ? 'day' : 'days'}
                        </div>
                    </div>
                ))}

                {allDays.filter(day => !standardDays.includes(day)).map((value) => (
                    <div key={value} className='flex flex-col items-center'>
                        <div className='w-full flex flex-row items-center justify-center text-[#95959] text-[7px] h-4'>
                            &nbsp;
                        </div>

                        <div className='relative group'>
                            <div
                                onClick={() => handleDayClick(value)}
                                onMouseDown={(e) => handleDayEdit(e, value)}
                                className={`w-[58px] h-[27px] xs:w-[54px] xs:h-[27px] rounded-[4px] border flex flex-row items-center justify-center text-[11px] cursor-pointer transition-all duration-200 ${
                                    selectedDay === value
                                        ? 'bg-[#353232] border-white/30 text-white'
                                        : 'bg-[#121212] border-[#353232] text-[#959595] hover:bg-[#1a1a1a] hover:border-white/20'
                                }`}
                                title="Ctrl+Click для редактирования"
                            >
                                {value} {value === 1 ? 'day' : 'days'}
                            </div>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(removeCustomDay(value));
                                    if (selectedDay === value) {
                                        dispatch(clearSelectedDay());
                                    }
                                }}
                                className='absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center transition-colors duration-200 hover:bg-red-600'
                                title="Удалить кастомный день"
                            >
                                <span className='text-white text-[8px] font-bold'>×</span>
                            </button>
                        </div>
                    </div>
                ))}

                {allDays.filter(day => !standardDays.includes(day)).length === 0 && (
                    <div className='flex flex-col items-center'>
                        <div className='w-full flex flex-row items-center justify-center text-[#95959] text-[7px] h-4'>
                            &nbsp;
                        </div>

                        <div
                            onClick={handleCustomDayClick}
                            className='w-[58px] h-[27px] bg-[#121212] rounded-[4px] border border-[#353232] flex flex-row items-center justify-center cursor-pointer hover:bg-[#1a1a1a] transition-colors'
                        >
                            <img width={13} height={13} src={getIcon('pen')} alt="edit custom day" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const BudgetAndToken = React.memo(function BudgetAndTokenInner({ onBudgetChange, selectedBlockchain, onBlockchainChange, foundToken, showSnackbar, closeSnackbar }) {
    const dispatch = useDispatch();
    const { budget, promoterWalletAddress } = useSelector((s) => s.budget);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValue, setEditValue] = React.useState('');
    const [tokenAdress, setTokenAdress] = React.useState('');
    const [detectedToken, setDetectedToken] = React.useState(null);
    const [possibleBlockchains, setPossibleBlockchains] = React.useState([]);
    const inputRef = React.useRef(null);
    const [hardhatConfig, setHardhatConfig] = React.useState(null);
    const { step, completePrizePool } = usePromoGuide();
    const showPrizeGuide = step === 'prizePool';
    useAdvanceGuideOnKeyboardClose(showPrizeGuide, completePrizePool);

    const bufferHandlerRef = React.useRef(null);
    const { openKeyboard } = useKeyboard();
    const blockChainsList = [
        // Неактивные сети: раскомментировать, когда контракты будут
        // задеплоены и в соответствующей сети (сейчас боевая — Arbitrum).
        // { key: 'ETH', name: 'ETH', fullName: 'Ethereum' },
        // { key: 'BNB', name: 'BSC', fullName: 'Binance' },
        // { key: 'solana', name: 'SOL', fullName: 'Solana' },
        // { key: 'sui', name: 'SUI', fullName: 'Sui' },
        // { key: 'base', name: 'BASE', fullName: 'Base' },
        // { key: 'TON', name: 'TON', fullName: 'TON' },
        // { key: 'polygon_chain', name: 'MATIC', fullName: 'Polygon' },
        // { key: 'optimism', name: 'OP', fullName: 'Optimism' },
        { key: 'arbitrum', name: 'ARB', fullName: 'Arbitrum' },
        // { key: 'avalanche', name: 'AVAX', fullName: 'Avalanche' },
        // Hardhat — тестовая сеть, скрывается на проде через REACT_APP_ENABLE_HARDHAT=false
        // (см. utils/featureFlags.js).
        ...hardhatChainOption()
    ]

    const defaultBudget = useMemo(() => [200, 300, 500, 800, 1000, 2000, 3000], []);
    const BUDGET_MIN = 0.001;

    const stripBudgetFormatting = (value) => String(value ?? '').replace(/\s/g, '').replace(',', '.');

    const formatBudgetThousands = (value) => {
        const stripped = stripBudgetFormatting(value);
        if (stripped === '' || stripped === '.') return stripped;

        const hasTrailingDot = stripped.endsWith('.');
        const [intPart = '', decPart] = stripped.split('.');
        const digitsOnly = intPart.replace(/\D/g, '');
        const formattedInt = digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

        if (stripped.includes('.')) {
            const dec = (decPart ?? '').replace(/\D/g, '');
            return `${formattedInt || '0'}.${dec}`;
        }
        if (hasTrailingDot) return `${formattedInt || '0'}.`;
        return formattedInt;
    };

    const isBudgetInputValid = (value) => /^\d*\.?\d*$/.test(stripBudgetFormatting(value));

    // Функция для определения возможных блокчейнов по адресу токена
    const detectPossibleBlockchains = (tokenAddress) => {
        if (!tokenAddress) return [];

        const trimmed = tokenAddress.trim();
        const possible = [];

        console.log('🔍 Analyzing token address:', trimmed);
        console.log('🔍 Length:', trimmed.length);

        // Тестируем Solana адрес напрямую
        const testSolanaAddress = '5areqv5tgjj8ubhnprc1wl5kmmijce4fuexcaexubags';
        console.log('🔍 Testing known Solana address:', testSolanaAddress);
        console.log('🔍 Known Solana regex test:', /^[1-9A-HJ-NP-Za-km-z]{32,50}$/.test(testSolanaAddress));
        console.log('🔍 Known Solana length:', testSolanaAddress.length);

        // Ethereum-подобные адреса (0x...)
        if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
            console.log('🔍 Ethereum-like address detected');
            possible.push('ETH', 'BNB', 'base', 'polygon_chain', 'optimism', 'arbitrum', 'avalanche');
        }

        // Solana адреса (Base58 формат) - разная длина
        if (/^[1-9A-HJ-NP-Za-km-z]{32,50}$/.test(trimmed) && !trimmed.startsWith('0x')) {
            console.log('🔍 Solana address detected');
            console.log('🔍 Solana regex test result:', /^[1-9A-HJ-NP-Za-km-z]{32,50}$/.test(trimmed));
            // Примеры: 71jvq4epe2fcj7jfsf7jlxdnk1wy4bhqd9il6befelvg (44)
            //           3JxzHjrCvTT9SXFkptC7MLf8pHDubYbnqq5Aj53Mpump (45)
            //           7olwgmugbbm9uwdmffsdxle98ychfah1udy5xpkylff8 (44)
            //           5areqv5tgjj8ubhnprc1wy4bhqd9il6befelvg (44)
            //           5areqv5tgjj8ubhnprc1wl5kmmijce4fuexcaexubags (44)
            possible.push('solana');
        }

        // TON адреса
        if (/^[0-9a-zA-Z_-]{48}$/.test(trimmed)) {
            console.log('🔍 TON address detected');
            possible.push('TON');
        }

        // Sui адреса (различные форматы)
        if (/^0x[a-fA-F0-9]{64}$/.test(trimmed) || // Обычный Sui адрес
            /^0x[a-fA-F0-9]{64}::[a-zA-Z0-9_]+::[a-zA-Z0-9_]+$/.test(trimmed)) { // Sui с модулем и функцией
            console.log('🔍 Sui address detected');
            // Примеры: 0x5960b1ccc59554482909cbbd3d756994ca63c2a5535adbbb772414321a5a7220::dmoon::dmoon
            //          0x0212c13fa071fcdae9f1f51bc4d820fa9f2396934478758bb278015e7781c154::rocket::rocket
            //          0x33fd7eeb8a5968f4f61e46783b70cae347b216d84a3c0106cccf815787f2720::starmie::starmie
            possible.push('sui');
        }

        console.log('🔍 Final possible blockchains:', possible);
        return possible;
    };

    const handleStartEdit = React.useCallback(() => {
        setIsEditing(true);
        setEditValue(formatBudgetThousands(String(budget)));
    }, [budget]);

    const persistBudget = React.useCallback((rawValue, { finalize = false } = {}) => {
        const normalized = stripBudgetFormatting(rawValue);
        if (normalized === '' || normalized === '.') {
            if (finalize) {
                setEditValue(formatBudgetThousands(String(budget)));
            }
            return;
        }

        const num = parseFloat(normalized);
        if (Number.isNaN(num)) return;

        if (finalize) {
            if (num < BUDGET_MIN) {
                showSnackbar(`Минимальный бюджет ${BUDGET_MIN}`, 'error');
                onBudgetChange?.(BUDGET_MIN);
                setEditValue(formatBudgetThousands(String(BUDGET_MIN)));
                setIsEditing(false);
                return;
            }
            onBudgetChange?.(num);
            setEditValue(formatBudgetThousands(String(num)));
            setIsEditing(false);
            return;
        }

        if (num > 0) {
            onBudgetChange?.(num);
        }
    }, [budget, onBudgetChange, showSnackbar]);

    React.useEffect(() => {
        if (!isEditing) {
            setEditValue(formatBudgetThousands(String(budget)));
        }
    }, [budget, isEditing]);

    const handleChange = React.useCallback((e) => {
        const raw = e.target.value;
        if (!isBudgetInputValid(raw)) return;
        const stripped = stripBudgetFormatting(raw);
        const formatted = formatBudgetThousands(stripped);
        setEditValue(formatted);
        setIsEditing(true);
        if (!stripped.endsWith('.')) {
            persistBudget(stripped);
        }
    }, [persistBudget]);

    const handleBlur = React.useCallback(() => {
        persistBudget(editValue, { finalize: true });
        setIsEditing(false);
    }, [editValue, persistBudget]);

    const handleKeyDown = React.useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            persistBudget(editValue, { finalize: true });
            inputRef.current?.blur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsEditing(false);
            setEditValue(formatBudgetThousands(String(budget)));
            inputRef.current?.blur();
        }
    }, [editValue, budget, persistBudget]);



    // Инициализация BufferHandler и слушание событий
    React.useEffect(() => {
        console.log('🔍 Initializing BufferHandler...');

        // Создаем экземпляр BufferHandler
        bufferHandlerRef.current = new BufferHandler();
        console.log('🔍 BufferHandler instance created');

        // Слушаем событие обнаружения токена
        const handleTokenDetected = (event) => {
            console.log('🔍 handleTokenDetected called with event:', event);
            const { tokenAddress } = event.detail;
            setDetectedToken(tokenAddress);

            // Определяем возможные блокчейны
            const possible = detectPossibleBlockchains(tokenAddress);
            console.log('🔍 Token detected:', tokenAddress);
            console.log('🔍 Possible blockchains:', possible);
            setPossibleBlockchains(possible);

            // Показываем уведомление
            showSnackbar(`Токен обнаружен в буфере! Нажмите на поле для вставки`, 'success');
        };

        document.addEventListener('tokenDetected', handleTokenDetected);
        console.log('🔍 Event listener added for tokenDetected');

        // Начинаем слушать буфер обмена
        bufferHandlerRef.current.startListening();
        console.log('🔍 BufferHandler started listening');

        return () => {
            document.removeEventListener('tokenDetected', handleTokenDetected);
            if (bufferHandlerRef.current) {
                bufferHandlerRef.current.stopListening();
            }
        };
    }, []);

    // Сети, для которых промо создаётся ON-CHAIN (нужен chainId и адрес токена).
    // chainId берём из конфига бэкенда, а не хардкодим: после переключения на
    // мейннет бэкенд отдаёт 42161, на деве — 31337.
    const isOnChainNetwork = selectedBlockchain === 'hardhat' || selectedBlockchain === 'arbitrum';

    React.useEffect(() => {
        if (isOnChainNetwork && !hardhatConfig) {
            blockchainApi.getConfig()
                .then(res => setHardhatConfig(res.data))
                .catch(err => console.error('Failed to load blockchain config:', err));
        }
    }, [isOnChainNetwork, hardhatConfig]);

    const fetchToken = async (tokenAddress = null) => {
        const addressToSearch = tokenAddress || tokenAdress.trim();

        if (!addressToSearch) {
            showSnackbar('Введите адрес токена', 'error');
            return;
        }

        const applyFoundToken = (tokenData) => {
            if (tokenData.blockchain && tokenData.blockchain !== selectedBlockchain) {
                const correctBlockchain = blockChainsList.find(b => b.key === tokenData.blockchain);
                if (correctBlockchain) {
                    onBlockchainChange(tokenData.blockchain);
                    showSnackbar(`Токен найден на ${correctBlockchain.fullName}! Блокчейн автоматически переключен.`, 'success', tokenData, correctBlockchain.fullName, true);
                } else {
                    showSnackbar(`Токен найден!`, 'success', tokenData, tokenData.blockchain, false);
                }
            } else {
                const blockchainInfo = blockChainsList.find(b => b.key === selectedBlockchain);
                showSnackbar(`Токен найден!`, 'success', tokenData, blockchainInfo?.fullName || selectedBlockchain, false);
            }
            dispatch(setFoundToken(tokenData));
        };

        try {
            const localData = await tokenApi.getTokenByAddress(addressToSearch);
            const local = localData?.data || localData;
            if (local && local.symbol) {
                applyFoundToken(local);
                return;
            }
        } catch (_) {}

        try {
            const response = await tokenApi.lookupToken(addressToSearch, selectedBlockchain.toLowerCase());
            const tokenData = response.data || response;
            if (tokenData && tokenData.symbol) {
                applyFoundToken(tokenData);
            } else {
                showSnackbar('Токен не найден. Проверьте блокчейн или адрес токена', 'error');
            }
        } catch (error) {
            console.error('Error fetching token:', error);
            showSnackbar('Токен не найден. Проверьте блокчейн или адрес токена', 'error');
        }
    }

    const openTokenAddressKeyboard = React.useCallback(() => {
        if (detectedToken) return;
        openKeyboard({
            type: 'qwerty',
            value: '',
            label: 'Token address',
            onChange: (finalValue) => {
                const trimmed = finalValue.trim();
                if (!trimmed) return;
                setTokenAdress(trimmed);
                setDetectedToken(null);
                setPossibleBlockchains([]);
                fetchToken(trimmed);
            },
            liveUpdate: false,
        });
    }, [detectedToken, openKeyboard, fetchToken]);

    // Держим фокус стабильно, даже если был ререндер (только после явного тапа)
    React.useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
            // ставим каретку в конец
            const len = inputRef.current.value?.length ?? 0;
            inputRef.current.setSelectionRange?.(len, len);
        }
    }, [isEditing, editValue]);

    return (
        <div data-guide="prizePool" className='w-full flex flex-col pb-2'>
            {/* <div className='w-full flex flex-row items-center justify-start pl-[42px]'>
                <span className='text-[9px] text-[#959595]'>min</span>
            </div> */}

            <div className='flex flex-row flex-wrap items-center justify-center px-4 xs:gap-[10px] gap-[14px] pb-4 border-b border-[#353232]'>
                {/* {defaultBudget.map((value, i) => {
                    // Прежняя логика скрытия второй строки при редактировании
                    const isInEditRow = i >= 4;
                    if (isEditing && isInEditRow) return null;

                    const isSelected = budget === value;

                    return (
                        <div
                            key={value}
                            onClick={() => {
                                // Проверяем минимальный бюджет
                                if (value < 200) {
                                    showSnackbar('Минимальный бюджет 200! Устанавливаю 200.', 'error');
                                    onBudgetChange?.(200);
                                } else {
                                    onBudgetChange?.(value);
                                }
                            }}
                            className={`w-[70px] xs:w-[60px]  h-[26px] rounded-[4px] flex flex-row items-center justify-center cursor-pointer transition-all duration-200 ${isSelected
                                ? 'bg-[#121212] border border-white/30'
                                : 'bg-[#121212]  border-white'
                                }`}
                        >
                            <span className={`text-[12px] ${isSelected ? 'text-white' : 'text-[#959595]'}`}>
                                {value}
                            </span>
                        </div>
                    );
                })} */}

                {/* {!isEditing ? (
                    <button
                        type='button'
                        className='w-[70px] xs:w-[60px] h-[26px] bg-[#121212] rounded-[4px] flex items-center justify-center cursor-pointer hover:bg-[#1a1a1a]'
                        onClick={handleStartEdit}
                    >
                        <img src={getIcon('pen')} alt='edit' className='w-4 h-4' />
                    </button>
                ) : (
                    <div
                     onClick={handleStartEdit}
                     className='flex flex-row items-center gap-[14px]'>
                        <input
                            ref={inputRef}
                            type='text'
                            value={editValue}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            inputMode='numeric'
                            pattern='[0-9]*'
                            className='w-[240px] h-[26px] bg-[#121212] rounded-[4px] px-2 text-white text-[12px] border border-[#353232] focus:border-white focus:outline-none'
                            placeholder='Введите бюджет'
                        />
                    </div>
                )} */}

                <div
                     onClick={handleStartEdit}
                     className="w-full flex flex-row items-center gap-[14px]">
                        <input
                            ref={inputRef}
                            type='text'
                            value={editValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={() => setIsEditing(true)}
                            onKeyDown={handleKeyDown}
                            inputMode='decimal'
                            enterKeyHint='done'
                            pattern='[0-9]*[.,]?[0-9]*'
                            className={`w-full h-[26px] bg-[#121212] rounded-[4px] px-2 text-white text-[12px] text-center border focus:outline-none ${
                                showPrizeGuide
                                    ? 'border-[#2373BE] focus:border-[#2373BE]'
                                    : 'border-[#353232] focus:border-white'
                            }`}
                            placeholder='Введите бюджет'
                        />
                </div>
            </div>
            {/*Blockchains  and token*/}
            <div className='w-full flex flex-row items-center h-[26px] px-4 py-6 gap-4'>
                <span className='text-[12px] text-white font-semi'>Blockchain</span>
                <div className='w-[80px] h-[26px] flex flex-row items-center justify-center border border-yellow-400 gap-1 rounded-[4px] px-1'>
                    <img
                        width={16}
                        height={16}
                        src={getIcon(selectedBlockchain)}
                        alt={blockChainsList.find(b => b.key === selectedBlockchain)?.name || 'Blockchain'}
                        className='flex-shrink-0 object-contain'
                    />
                    <span className='text-[10px] text-white font-medium'>
                        {blockChainsList.find(b => b.key === selectedBlockchain)?.fullName || 'Blockchain'}
                    </span>
                </div>
                <span className='text-[12px] text-white font-semi'>Token</span>
                <div className='w-[80px] h-[26px] flex-shrink-0 overflow-hidden flex flex-row items-center border border-yellow-400 gap-1 rounded-[4px] pl-1 pr-1'>
                    {foundToken ? (
                        <>
                            <img
                                width={15}
                                height={15}
                                src={`${API_BASE_URL}/${foundToken.logoPath}`}
                                alt={foundToken.symbol}
                                className='flex-shrink-0 object-contain rounded-full'
                            />
                            <span className='text-[7px] text-white font-medium truncate min-w-0 flex-1' title={foundToken.symbol}>
                                {foundToken.symbol}
                            </span>
                        </>
                    ) : (
                        <>
                            <img
                                width={15}
                                height={15}
                                src={getIcon('img')}
                                alt="USDT"
                                className='flex-shrink-0 object-contain'
                            />
                            <span className='text-[9px] text-white font-medium truncate min-w-0 flex-1'>Unknown</span>
                        </>
                    )}
                </div>

            </div>
            {/*Blockchains list*/}
            <div className='w-full flex flex-row flex-wrap items-center justify-center gap-2 px-4'>
                {blockChainsList.map((value, i) => {
                    const isSelected = selectedBlockchain === value.key;
                    const isPossible = possibleBlockchains.includes(value.key);

                    return (
                        <div
                            key={i}
                            onClick={() => onBlockchainChange(value.key)}
                            className={`relative w-[58px] xs:w-[50px] h-[22px] flex flex-row items-center justify-center rounded-[4px] border gap-1 px-1 cursor-pointer transition-all duration-300 ${isSelected
                                ? 'border-white bg-white/10'
                                : isPossible
                                    ? 'border-green-400/60 bg-green-500/10 hover:border-green-400 hover:bg-green-500/20'
                                    : 'border-[#353232] hover:border-white/50'
                                }`}
                        >
                            <img
                                width={16}
                                height={16}
                                src={getIcon(value.key)}
                                alt={value.name}
                                className='flex-shrink-0 object-contain xs:w-[13px] xs:h-[13px]'
                            />
                            <span className='text-[10px] xs:text-[9px] text-white text-center leading-none whitespace-nowrap overflow-hidden'>
                                {value.name}
                            </span>

                            {/* Индикатор возможного блокчейна */}
                            {isPossible && !isSelected && (
                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            )}
                        </div>
                    )
                })}
            </div>

            <div className='flex flex-row items-center px-4 mt-4'>
                <div
                    className={`relative w-full h-[26px] rounded-[4px] border transition-all duration-300 cursor-pointer ${detectedToken
                        ? 'bg-gradient-to-r from-green-500/20 to-blue-500/20 border-green-400/50 hover:border-green-400'
                        : showPrizeGuide
                            ? 'bg-[#121212] border-[#2373BE]'
                            : 'bg-[#121212] border-[#353232] hover:border-white/50'
                        }`}
                    onClick={() => {
                        if (detectedToken) {
                            const tokenToInsert = detectedToken;
                            setTokenAdress(tokenToInsert);
                            setDetectedToken(null);
                            setPossibleBlockchains([]);
                            fetchToken(tokenToInsert);
                        }
                    }}
                    title={detectedToken ? `Нажмите для вставки токена: ${detectedToken.slice(0, 12)}...` : 'Введите адрес токена'}
                >
                    <input
                        type='text'
                        value={tokenAdress}
                        readOnly
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!detectedToken) {
                                openTokenAddressKeyboard();
                            }
                        }}
                        className='w-full h-full px-2 bg-transparent text-white text-[12px] focus:outline-none cursor-pointer'
                        placeholder={detectedToken ? 'Нажмите для вставки токена из буфера' : '0x0...     token address'}
                    />

                    {detectedToken && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-[8px] text-green-400 font-medium">TOKEN</span>
                        </div>
                    )}
                </div>
            </div>

            {selectedBlockchain === 'hardhat' && (
                <>
                    {hardhatConfig && (
                        <div className='flex flex-row items-center px-4 mt-3 gap-2'>
                            <span className='text-[10px] text-[#959595]'>Quick:</span>
                            {hardhatConfig.mockUsdtAddress && (
                                <div
                                    className='h-[22px] px-2 bg-[#121212] border border-[#353232] rounded-[4px] flex items-center cursor-pointer hover:border-white/30 transition-colors'
                                    onClick={() => {
                                        setTokenAdress(hardhatConfig.mockUsdtAddress);
                                        dispatch(setFoundToken({ address: hardhatConfig.mockUsdtAddress, symbol: 'mUSDT', name: 'Mock USDT', decimals: 6 }));
                                        showSnackbar('MockUSDT selected', 'success');
                                    }}
                                >
                                    <span className='text-[10px] text-white'>mUSDT</span>
                                </div>
                            )}
                            {hardhatConfig.mockMemeAddress && (
                                <div
                                    className='h-[22px] px-2 bg-[#121212] border border-[#353232] rounded-[4px] flex items-center cursor-pointer hover:border-white/30 transition-colors'
                                    onClick={() => {
                                        setTokenAdress(hardhatConfig.mockMemeAddress);
                                        dispatch(setFoundToken({ address: hardhatConfig.mockMemeAddress, symbol: 'mMEME', name: 'Mock Meme', decimals: 18 }));
                                        showSnackbar('MockMEME selected', 'success');
                                    }}
                                >
                                    <span className='text-[10px] text-white'>mMEME</span>
                                </div>
                            )}
                        </div>
                    )}

                </>
            )}

        </div>
    );
});

const Categories = React.memo(function CategoriesInner({ crystals, icons, selectedId, loading, error, onSelect }) {
    const { step, completeCategories } = usePromoGuide();
    const showCategoriesGuide = step === 'categories';

    const handleSelect = (id) => {
        onSelect(id);
    };

    if (loading) return <p className='text-white text-[12px]'>Загрузка...</p>;
    if (error) return <p className='text-red-500 text-[12px]'>Ошибка: {error}</p>;

    const positionColors = ['#BC3EA4', '#DA2223', '#1FAFC3', '#1AC152', '#FFB700', '#B3A4A4', '#894F24'];
    
    // Функция для получения цвета первой позиции
    const getColorForNumber = (number) => {
        if (number === 7) return positionColors[0]; // BC3EA4
        if (number === 5) return positionColors[2]; // 1FAFC3
        if (number === 3) return positionColors[4]; // FFB700
        return positionColors[0];
    };

    const crystalsWithNumber = crystals.filter(item => item.number !== undefined && item.number !== null);


    if (crystalsWithNumber.length === 0) {
        return (
            <>
                <div data-guide="categories" className='flex w-full pb-[16px] px-[16px] pt-[0px] flex-row justify-center gap-[10px]'>
                    <div className='flex flex-col items-center gap-[8px] xs:gap-6px'>
                        {[...crystals].reverse().map((item) => {
                            const isSelected = selectedId === item.id;
                            const color = getColorForNumber(item.number);
                            
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleSelect(item.id)}
                                    className='flex flex-col items-center justify-center w-[104px] h-[70px] gap-[8px] cursor-pointer'
                                >
                                    <div 
                                        className="w-[30px] h-[30px] rounded-full flex items-center justify-center"
                                        style={{ borderColor: color, borderWidth: '1px' }}
                                    >
                                        <span className="text-[14px] font-semibold" style={{ color: color }}>{item.number}</span>
                                    </div>
                                    <div
                                        className={`w-[104px]  h-[32px]  px-[14px] py-[7px] gap-[6px] text-[14px] rounded-[8px] whitespace-nowrap 
                        flex items-center justify-center
                        ${isSelected ? 'border border-white' : 'bg-white/15 border border-white/10'}`}
                                    >
                                        {item.name}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </>
        );
    }

    const grouped = crystalsWithNumber.reduce((acc, item) => {
        (acc[item.number] ||= []).push(item);
        return acc;
    }, {});
    const sortedNumbers = Object.keys(grouped).sort((a, b) => Number(b) - Number(a));

    return (
        <>
            <div data-guide="categories" className='flex w-full pb-[16px] xs:px-[13px] px-[16px] pt-[0px] flex-row justify-center gap-[10px]'>
                {[...sortedNumbers].reverse().map((number) => (
                    <div key={number} className='flex flex-col items-center gap-[8px]'>
                        {grouped[number].map((item) => {
                            const isSelected = selectedId === item.id;
                            const color = getColorForNumber(item.number);
                            
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleSelect(item.id)}
                                    className='flex flex-col items-center justify-center w-[104px] xs:w-[95px] h-[70px] gap-[8px] cursor-pointer'
                                >
                                    <div 
                                        className="w-[30px] h-[30px] rounded-full flex items-center justify-center xs:w-[26px] xs:h-[26px]"
                                        style={{ borderColor: color, borderWidth: '1px' }}
                                    >
                                        <span className="text-[14px] xs:text-[12px] font-semibold" style={{ color: color }}>{item.number}</span>
                                    </div>
                                    <div
                                        className={`w-[104px] xs:w-[96px] h-[32px] px-[14px] py-[7px] gap-[6px] xs:text-[12px]  text-[14px] rounded-[8px] whitespace-nowrap 
                        flex items-center justify-center
                        ${isSelected ? 'border border-white' : 'bg-white/15 border border-white/10'}`}
                                    >
                                        <SettingsIcon/>
                                        {item.name}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </>
    );
});

const PrizePoolFormAccordion = (props) => {
    const { step } = usePromoGuide();
    return <FormAccardion {...props} isOpen={(step === 'prizePool' || step === 'players' || step === 'winners' || step === 'tierWinners' || step === 'tierBudget' || step === 'telegramLink') ? true : undefined} />;
};

const CategoriesFormAccordion = (props) => {
    const { step } = usePromoGuide();
    return <FormAccardion {...props} isOpen={step === 'categories' ? true : undefined} />;
};

const PromoTimeFormAccordion = (props) => {
    const { step } = usePromoGuide();
    return <FormAccardion {...props} isOpen={step === 'promoTime' ? true : undefined} />;
};

const TasksFormAccordion = (props) => {
    const { step, completeAddTasks } = usePromoGuide();
    const showAddTasksGuide = step === 'addTasks';
    useAdvanceGuideOnKeyboardClose(showAddTasksGuide, completeAddTasks);

    return (
        <div>
            <FormAccardion {...props} isOpen={showAddTasksGuide ? true : undefined} />
        </div>
    );
};

export default function PromoFormOnlyCrypto() {
    const dispatch = useDispatch();
    const { requestLeave } = usePromoLeaveGuard();
    
    // Edit mode
    const { promo: editPromo, isEditMode, editMode } = useSelector(s => s.selectedPromo);
    const disabledDefaultTaskIds = useSelector((s) => s.tasks?.disabledDefaultTaskIds || []);
    const isLimited = editMode === 'limited';
    
    const forceExitPromoForm = () => {
        dispatch(clearEditMode());
        clearPromoCreationState(dispatch);
        dispatch(goBack());
    };

    const handleClose = () => {
        requestLeave(forceExitPromoForm);
    };

    const createNewPromo = async () => {
        try {
            // Проверка обязательных полей
            if (!selectedCategory) {
                showSnackbar('Выберите категорию', 'error');
                return;
            }
            if (!currentBudget || currentBudget <= 0) {
                showSnackbar('Установите бюджет промо', 'error');
                return;
            }
            if (!selectedBlockchain) {
                showSnackbar('Выберите блокчейн', 'error');
                return;
            }
            if (!foundToken || !foundToken.address) {
                showSnackbar('Выберите токен', 'error');
                return;
            }
            if (!link || link.trim() === '' || link === 'Promoter link') {
                showSnackbar('Введите ссылку на Telegram канал', 'error');
                return;
            }
            if (!data || !data.name) {
                showSnackbar('Загрузите данные промоутера (нажмите Enter в поле ссылки)', 'error');
                return;
            }
            if (!selectedDay) {
                showSnackbar('Выберите длительность промо', 'error');
                return;
            }

            const effectiveParticipants = customParticipants || selectedCategory?.minParticipantsCount || 0;
            const effectiveWinners = customWinners || selectedCategory?.minPrizePlacesCount || 0;
            const presets = selectedCategory?.presets || [];
            const placeWinners =
                Array.isArray(customPlaceWinners)
                && customPlaceWinners.length === presets.length
                && isMatchingPlaceTotals(customPlaceWinners, effectiveWinners, 0)
                    ? customPlaceWinners.map((n) => Number(n) || 0)
                    : distributeWinnersByPercent(presets, effectiveWinners);

            const placeBudgets =
                Array.isArray(customPlaceBudgets)
                && customPlaceBudgets.length === presets.length
                && isMatchingPlaceTotals(customPlaceBudgets, currentBudget)
                    ? customPlaceBudgets.map((n) => Number(n) || 0)
                    : distributeBudgetByPercent(presets, currentBudget);

            const winDefaults = resolveWinPercents(presets);
            const budgetDefaults = resolveBudgetPercents(presets);
            const winPercents = placeWinners.length
                ? distributePercentsFromCounts(placeWinners)
                : winDefaults;
            const budgetPercents = placeBudgets.length
                ? distributePercentsFromCounts(placeBudgets)
                : budgetDefaults;

            const positions = presets.map((preset, index) => {
                const winners = placeWinners[index] ?? Math.round((winDefaults[index] / 100) * effectiveWinners);
                const budgetAmount = placeBudgets[index] ?? Math.round((budgetDefaults[index] / 100) * currentBudget);
                const dropPerWinner = winners > 0 ? budgetAmount / winners : 0;
                const winPercent = winPercents[index] || 0;
                const budgetPercent = budgetPercents[index] || 0;

                return {
                    winPercent,
                    winners,
                    budgetPercent,
                    budget: budgetAmount,
                    dropPerWinner: parseFloat(dropPerWinner.toFixed(4))
                };
            });

            if (positions.length === 0) {
                showSnackbar('Категория не содержит позиций', 'error');
                return;
            }

            // Явно устанавливаем тип промо для Only Crypto
            const promoTypeValue = "cryptoOnly";
            
            // On-chain промо: chainId из конфига бэкенда (мейннет 42161 / dev 31337).
            // Без загруженного конфига промо создалось бы как НЕ-блокчейн и депозит
            // не заработал бы — поэтому лучше остановиться и сказать об этом.
            const chainIdFromConfig = Number(chainConfig?.chainId) || null;
            if (isOnChainNetwork && !chainIdFromConfig) {
                showSnackbar('Не удалось загрузить настройки сети. Попробуйте ещё раз.', 'error');
                return;
            }

            const promoData = {
                title: `Promo ${Date.now()}`,
                participantsCount: effectiveParticipants,
                prizePlacesCount: effectiveWinners,
                totalAmount: currentBudget,
                currentAmount: currentBudget,
                status: "PENDING",
                winnersCount: 0,
                durationDays: selectedDay || 3,
                tokenType: foundToken?.address || "",
                creatorUserId: 1,
                link: link.trim(),
                maxParticipantsCount: effectiveParticipants,
                category: {
                    category_name: selectedCategory?.name || "Unknown",
                    positions: positions,
                },
                promoType: promoTypeValue,
                promoters: data ? [data] : [],
                selectedBlockchain: selectedBlockchain,
                foundToken: foundToken,
                selectedCrystalCategory: selectedCategory,
                promoTime: selectedDay,
                tasks: [],
                disabledDefaultTaskIds,
                ...(isOnChainNetwork && {
                    chainId: chainIdFromConfig,
                    tokenContractAddress: foundToken?.address || "",
                    promoterAddress: promoterWalletAddress || "",
                }),
            };
            // Финальная проверка promoType перед отправкой
            if (promoData.promoType !== "cryptoOnly") {
                console.error('ОШИБКА: promoType должен быть "cryptoOnly", но получен:', promoData.promoType);
                promoData.promoType = "cryptoOnly"; // Принудительно устанавливаем правильное значение
            }
            
            console.log('Отправка данных промо (Only Crypto):', promoData);
            console.log('promoType установлен как:', promoData.promoType);
            console.log('Проверка promoType === "cryptoOnly":', promoData.promoType === "cryptoOnly");
            
            const response = await PromosApi.create(promoData);
            console.log('Промо создано:', response);

            // Создаём задачи для нового промо (если есть)
            const createdPromoId = response?.id;
            if (createdPromoId && tasks.length > 0) {
                try {
                    const tasksForApi = tasks.map(task => ({
                        title: task.text,
                        details: task.link,
                    }));
                    await TaskApi.createTasksForPromo(createdPromoId, tasksForApi);
                    console.log(`Задачи (${tasksForApi.length}) созданы для промо ${createdPromoId}`);
                } catch (taskErr) {
                    console.error('Ошибка создания задач:', taskErr);
                    showSnackbar('Промо создано, но задачи не сохранены. Можно добавить через редактирование.', 'error');
                    forceExitPromoForm();
                    return;
                }
            }

            // Переходим на страницу депозита с новым промо
            showSnackbar('Promo saved', 'success');
            dispatch(setSelectedPromo(response));
            dispatch(setPaymentType('deposit'));
            dispatch(clearEditMode());
            clearPromoFormFields(dispatch);
            setTimeout(() => {
                dispatch(setPage({ id: 7, name: 'payment' }));
            }, 1200);

        } catch (error) {
            console.error('Ошибка создания промо:', error);
            const errorMessage = error.response?.data?.message 
                || error.response?.data?.error 
                || error.message 
                || 'Ошибка создания промо';
            console.error('Детали ошибки:', error.response?.data);
            showSnackbar(errorMessage, 'error');
        }
    };

    const handleUpdatePromo = async () => {
        if (!editPromo?.id) {
            showSnackbar('Нет данных промо для обновления', 'error');
            return;
        }
        
        try {
            if (!isLimited) {
                // Full mode - можно менять budget, token, duration, category, etc.
                const editEffectiveWinners = customWinners || editPromo.prizePlacesCount || selectedCategory?.minPrizePlacesCount || 0;
                const presets = selectedCategory?.presets || [];
                const placeWinners =
                    Array.isArray(customPlaceWinners)
                    && customPlaceWinners.length === presets.length
                    && isMatchingPlaceTotals(customPlaceWinners, editEffectiveWinners, 0)
                        ? customPlaceWinners.map((n) => Number(n) || 0)
                        : distributeWinnersByPercent(presets, editEffectiveWinners);

                const placeBudgets =
                    Array.isArray(customPlaceBudgets)
                    && customPlaceBudgets.length === presets.length
                    && isMatchingPlaceTotals(customPlaceBudgets, currentBudget)
                        ? customPlaceBudgets.map((n) => Number(n) || 0)
                        : distributeBudgetByPercent(presets, currentBudget);

                const winDefaults = resolveWinPercents(presets);
                const budgetDefaults = resolveBudgetPercents(presets);
                const winPercents = placeWinners.length
                    ? distributePercentsFromCounts(placeWinners)
                    : winDefaults;
                const budgetPercents = placeBudgets.length
                    ? distributePercentsFromCounts(placeBudgets)
                    : budgetDefaults;

                const positions = presets.map((preset, index) => {
                    const winners = placeWinners[index] ?? Math.round((winDefaults[index] / 100) * editEffectiveWinners);
                    const budgetAmount = placeBudgets[index] ?? Math.round((budgetDefaults[index] / 100) * currentBudget);
                    const dropPerWinner = winners > 0 ? budgetAmount / winners : 0;
                    const winPercent = winPercents[index] || 0;
                    const budgetPercent = budgetPercents[index] || 0;
                    return {
                        winPercent,
                        winners,
                        budgetPercent,
                        budget: budgetAmount,
                        dropPerWinner: parseFloat(dropPerWinner.toFixed(4))
                    };
                });

                const editEffectiveParticipants = customParticipants || editPromo.participantsCount || selectedCategory?.minParticipantsCount || 0;
                
                const updateData = {
                    participantsCount: editEffectiveParticipants,
                    prizePlacesCount: editEffectiveWinners,
                    maxParticipantsCount: editEffectiveParticipants,
                    totalAmount: currentBudget,
                    currentAmount: currentBudget,
                    durationDays: selectedDay || editPromo.durationDays,
                    tokenType: foundToken?.address || editPromo.tokenType,
                    link: link?.trim() || editPromo.link,
                    category: {
                        category_name: selectedCategory?.name || editPromo.category?.category_name,
                        positions: positions.length > 0 ? positions : editPromo.category?.positions,
                    },
                    promoters: data ? [data] : editPromo.promoters,
                };
                
                await PromosApi.update(editPromo.id, updateData);
                
                // Сохраняем задачи только если они были загружены или изменены
                if (tasksLoaded || tasksModified) {
                    const tasksForApi = tasks.map(task => ({
                        title: task.text,
                        details: task.link,
                    }));
                    await TaskApi.createTasksForPromo(editPromo.id, tasksForApi);
                }
            } else {
                // Limited mode - можно менять tasks и duration
                const updateData = {
                    durationDays: selectedDay || editPromo.durationDays,
                };
                await PromosApi.update(editPromo.id, updateData);
                
                // Сохраняем задачи только если они были загружены или изменены
                if (tasksLoaded || tasksModified) {
                    const tasksForApi = tasks.map(task => ({
                        title: task.text,
                        details: task.link,
                    }));
                    await TaskApi.createTasksForPromo(editPromo.id, tasksForApi);
                }
            }
            
            showSnackbar('Промо успешно обновлено!', 'success');
            forceExitPromoForm();
            
        } catch (error) {
            console.error('Ошибка обновления промо:', error);
            const errorMessage = error.response?.data?.message 
                || error.response?.data?.error 
                || error.message 
                || 'Ошибка обновления промо';
            showSnackbar(errorMessage, 'error');
        }
    };
    
    const { items: crystals, icons, selectedId, loading, error } = useSelector((s) => s.crystals);
    const tasks = useSelector((s) => s.tasks?.items || []);
    const tasksLoaded = useSelector((s) => s.tasks?.tasksLoaded || false);
    const tasksModified = useSelector((s) => s.tasks?.tasksModified || false);
    const tasksCount = tasks.length;
    const { link, data } = useSelector((s) => s.promoTelegram);
    const selectedCategory = crystals.find(cat => cat.id === selectedId);
    const customParticipants = useSelector(s => s.promoForm.participantsCount);
    const customWinners = useSelector(s => s.promoForm.prizePlacesCount);
    const customPlaceWinners = useSelector(s => s.promoForm.customPlaceWinners);
    const customPlaceBudgets = useSelector(s => s.promoForm.customPlaceBudgets);
    const hydratedEditKeyRef = useRef(null);

    useEffect(() => {
        if (loading) hydratedEditKeyRef.current = null;
    }, [loading]);

    useEffect(() => {
        if (!isEditMode) {
            hydratedEditKeyRef.current = null;
            return;
        }
        // Wait for this form's fetchCrystals — stale list from another type left
        // category/players/winners empty after selectedId was cleared on fulfill.
        if (!editPromo?.id || loading || crystals.length === 0) return;

        const crystalsKey = crystals.map((c) => c.id).join(',');
        const hydrateKey = `${editPromo.id}:${crystalsKey}`;
        if (hydratedEditKeyRef.current === hydrateKey) return;
        hydratedEditKeyRef.current = hydrateKey;

        const matchingCategory = findCrystalCategoryForPromo(crystals, editPromo);
        const participants = editPromo.participantsCount;
        const winners = editPromo.prizePlacesCount;
        const max = editPromo.maxParticipantsCount || participants;
        const budget = editPromo.totalAmount;
        const blockchain = editPromo.selectedBlockchain;
        const days = editPromo.durationDays;

        // Counts before category so syncWithCrystals won't replace them with defaults
        if (participants) dispatch(setParticipantsCount(participants));
        if (winners) dispatch(setPrizePlacesCount(winners));
        if (max) dispatch(setMaxParticipantsCount(max));
        if (budget) dispatch(setBudget(budget));
        if (blockchain) dispatch(setSelectedBlockchain(blockchain));
        if (days) dispatch(selectDay(days));

        if (matchingCategory) {
            dispatch(setSelectedCrystalCategory(matchingCategory));
            dispatch(setSelectedId(matchingCategory.id));
        }
    }, [isEditMode, editPromo, crystals, loading, dispatch]);

    const type = 'onlyCrypto';

    const { budget: currentBudget, selectedBlockchain, foundToken, promoterWalletAddress } = useSelector((s) => s.budget);

    // Конфиг сети от бэкенда (chainId, адреса контрактов). Нужен, чтобы промо
    // создалось как ON-CHAIN и не хардкодить сеть в аппке.
    const [chainConfig, setChainConfig] = React.useState(null);
    React.useEffect(() => {
        blockchainApi.getConfig()
            .then(res => setChainConfig(res.data))
            .catch(err => console.error('Failed to load blockchain config:', err));
    }, []);
    const isOnChainNetwork = selectedBlockchain === 'hardhat' || selectedBlockchain === 'arbitrum';
    const selectedDay = useSelector(selectSelectedDay);

    const [snackbar, setSnackbar] = React.useState({ isVisible: false, message: '', type: 'error', tokenData: null, blockchainName: '', isBlockchainSwitched: false });
    const [snackbarAnimation, setSnackbarAnimation] = React.useState(false);

    const closeSnackbar = React.useCallback(() => {
        setSnackbarAnimation(false);
        setTimeout(() => {
            setSnackbar(prev => ({ ...prev, isVisible: false }));
        }, 300);
    }, []);

    const showSnackbar = React.useCallback((message, type, tokenData = null, blockchainName = '', isBlockchainSwitched = false) => {
        setSnackbar({ isVisible: true, message, type, tokenData, blockchainName, isBlockchainSwitched });
        setTimeout(() => setSnackbarAnimation(true), 100);
    }, []);

    React.useEffect(() => {
        if (snackbar.isVisible) {
            const timer = setTimeout(closeSnackbar, 4000);
            return () => clearTimeout(timer);
        }
    }, [snackbar.isVisible, closeSnackbar]);

    const isFormComplete = useMemo(() => {
        return (
            currentBudget > 0 &&                    // Бюджет выбран
            selectedBlockchain &&                    // Блокчейн выбран
            foundToken &&                           // Токен найден
            selectedId &&                           // Категория кристаллов выбрана
            (selectedDay || 3) &&                   // Дни промо (дефолт 3)
            link && link.trim() !== '' &&           // Ссылка на промотера заполнена
            link !== 'Promoter link' && // Не дефолтное значение
            data && data.name                       // Данные промоутера загружены
        );
    }, [currentBudget, selectedBlockchain, foundToken, selectedId, selectedDay, link, data]);

    const canSubmitPromo = isFormComplete && tasksCount > 0;

    const hasPromoter = Boolean(
        link && link.trim() !== '' && link !== 'Promoter link' && data?.name
    );

    const handleFinishClick = React.useCallback(() => {
        if (canSubmitPromo) {
            if (isEditMode) handleUpdatePromo();
            else createNewPromo();
            return;
        }

        const parts = [];
        if (!hasPromoter) parts.push('· Add a Promoter');
        if (tasksCount <= 0) parts.push('· Add at least one Task');
        if (parts.length === 0) {
            if (!(currentBudget > 0) || !foundToken || !selectedBlockchain) {
                parts.push('· Complete Prize Pool');
            }
            if (!selectedId) parts.push('· Select reward categories');
            if (!selectedDay) parts.push('· Set Promo time');
        }

        showSnackbar(parts.join('  ') || '· Complete all fields', 'hint');
    }, [
        canSubmitPromo,
        isEditMode,
        handleUpdatePromo,
        createNewPromo,
        hasPromoter,
        tasksCount,
        currentBudget,
        foundToken,
        selectedBlockchain,
        selectedId,
        selectedDay,
        showSnackbar,
    ]);

    useEffect(() => {
        dispatch(fetchCrystals('cryptoOnly'));
    }, [dispatch]);

    // Загрузка токена по адресу при редактировании
    useEffect(() => {
        const loadToken = async () => {
            if (!isEditMode || !editPromo) return;
            
            // Если уже есть foundToken в промо - используем его
            if (editPromo.foundToken) {
                dispatch(setFoundToken(editPromo.foundToken));
                return;
            }
            
            // Иначе загружаем по tokenType (адрес токена)
            const tokenAddress = editPromo.tokenType;
            if (!tokenAddress) return;
            
            try {
                // Определяем блокчейн для поиска
                const blockchain = editPromo.selectedBlockchain || 'sui';
                const response = await tokenApi.lookupToken(tokenAddress, blockchain.toLowerCase());
                const tokenData = response.data || response;
                
                if (tokenData && tokenData.symbol) {
                    dispatch(setFoundToken(tokenData));
                    // Если блокчейн отличается - обновляем
                    if (tokenData.blockchain && tokenData.blockchain !== editPromo.selectedBlockchain) {
                        dispatch(setSelectedBlockchain(tokenData.blockchain));
                    }
                }
            } catch (error) {
                console.error('[EditMode] Failed to load token:', error);
            }
        };
        
        loadToken();
    }, [isEditMode, editPromo, dispatch]);

    // Загрузка данных промоутера при редактировании
    useEffect(() => {
        if (!isEditMode || !editPromo) return;
        
        // Устанавливаем ссылку в promoFormSlice
        if (editPromo.link) {
            dispatch(setFormLink(editPromo.link));
        }
        
        // Устанавливаем данные промоутера из промо
        const promoter = editPromo.promoters?.[0];
        if (promoter) {
            const logoPath = promoter.logo || promoter.logoPath || '';
            const logoUrl = logoPath.startsWith('http') 
                ? logoPath 
                : logoPath.startsWith('/') 
                    ? `${API_BASE_URL}${logoPath}` 
                    : logoPath ? `${API_BASE_URL}/${logoPath}` : '';
            
            dispatch(setPromoterData({
                name: promoter.name || promoter.title,
                logo: logoUrl,
                link: editPromo.link,
            }));
        } else if (editPromo.link) {
            // Если нет данных промоутера но есть ссылка - устанавливаем хотя бы ссылку
            dispatch(setPromoterData({
                name: null,
                logo: null,
                link: editPromo.link,
            }));
        }
    }, [isEditMode, editPromo, dispatch]);

    // Загрузка задач при редактировании
    useEffect(() => {
        if (!isEditMode || !editPromo?.id) return;
        
        const loadTasks = async () => {
            try {
                const tasksFromApi = await TaskApi.getTasksByPromo(editPromo.id);
                if (tasksFromApi && tasksFromApi.length > 0) {
                    const formattedTasks = tasksFromApi.map(task => ({
                        text: task.title,
                        label: task.details?.includes('telegram') ? 'telegram' : 
                               task.details?.includes('twitter') || task.details?.includes('x.com') ? 'twitter' : 'link',
                        link: task.details || '',
                        isComplated: false,
                    }));
                    dispatch(setTasks(formattedTasks));
                } else {
                    dispatch(setTasks([]));
                }
            } catch (error) {
                console.error('Ошибка загрузки задач:', error);
            }
        };
        
        loadTasks();
    }, [isEditMode, editPromo?.id, dispatch]);

    useEffect(() => {
        if (!loading && !error && crystals.length > 0 && selectedId == null && !isEditMode) {
            const crystalsWithNumber = crystals.filter(item => item.number !== undefined && item.number !== null);

            if (crystalsWithNumber.length > 0) {
                const sortedByNumber = [...crystalsWithNumber].sort((a, b) => a.number - b.number);
                const defaultCategory = sortedByNumber[0];
                dispatch(setSelectedId(defaultCategory.id));
            } else if (crystals.length > 0) {
                dispatch(setSelectedId(crystals[0].id));
            }
        }
    }, [loading, error, crystals, selectedId, dispatch, isEditMode]);

    const onSelectCategory = React.useCallback((id) => {
        dispatch(setSelectedId(id));
    }, [dispatch]);

    const onBudgetChange = React.useCallback((newBudget) => {
        dispatch(setBudget(newBudget));
    }, [dispatch]);

    const onBlockchainChange = React.useCallback((blockchainKey) => {
        dispatch(setSelectedBlockchain(blockchainKey));
    }, [dispatch]);

    const categoriesContent = useMemo(() => {
        return (
            <Categories
                crystals={crystals}
                icons={icons}
                selectedId={selectedId}
                loading={loading}
                error={error}
                onSelect={onSelectCategory}
            />
        );
    }, [crystals, icons, selectedId, loading, error, onSelectCategory]);

    return (
        <PromoGuideProvider initialStep={isEditMode ? null : 'prizePool'}>
        <div className='w-full h-full flex flex-col overflow-y-auto pt-8'>
            <PromoTypeIntroBanner
                enabled={!isEditMode}
                variant="cryptoOnly"
                title={'Crypto only Promo'}
                lines={['You can customize your reward tiers and winners. Follow the Guide.']}
            />
            <PromoGuideTour />
            <GuideSkipButton />
            {/* <div className="pt-[24px] pb-[10px]">
                <Header
                    leftButtonText="Close"
                    centerTitle={isEditMode ? "Edit Promo" : "Create Promo"}
                    onLeftClick={handleClose}
                    useBackIcon={false}
                />
            </div> */}
            
            {/* Предупреждение при ограниченном режиме */}
            {isEditMode && isLimited && (
                <div className="mx-[15px] mb-2 p-3 bg-[#FFC813]/20 border border-[#FFC813] rounded-lg">
                    <span className="text-[#FFC813] text-[12px]">
                        ⚠️ Promo is paid. You can only edit tasks and duration.
                    </span>
                </div>
            )}
            
            <div className='flex-1 flex flex-col gap-[8px] px-[15px] pt-[20px] pb-[120px]'>
            <PromoForm Type={type} isEditMode={isEditMode} isLimited={isLimited} />

            <div className='flex flex-col gap-[8px]'>
                <PrizePoolFormAccordion
                    number={1}
                    title='Prize pool'
                    child={
                        <div className='w-[100px]  h-[26px] '>
                            <CoinBadge
                                token={foundToken ? `${API_BASE_URL}/${foundToken.logoPath}` : undefined}
                                icon={foundToken ? undefined : 'img'}
                                count={currentBudget}
                                iconHeight={22}
                                iconWidth={22}
                            />
                        </div>
                    }
                    content={<BudgetAndToken onBudgetChange={onBudgetChange} selectedBlockchain={selectedBlockchain} onBlockchainChange={onBlockchainChange} foundToken={foundToken} showSnackbar={showSnackbar} closeSnackbar={closeSnackbar} />}
                    disabled={isLimited}
                />

                <CategoriesFormAccordion
                    number={2}
                    title='Reward Categories'
                    child={
                        <div className='flex flex-row items-center  justify-start gap-1'>
                            {selectedId && crystals.find((c) => c.id === selectedId) ? (
                                (() => {
                                    const selectedCat = crystals.find((c) => c.id === selectedId);
                                    const positionColors = ['#BC3EA4', '#DA2223', '#1FAFC3', '#1AC152', '#FFB700', '#B3A4A4', '#894F24'];
                                    const getColorForNumber = (number) => {
                                        if (number === 7) return positionColors[0];
                                        if (number === 5) return positionColors[2];
                                        if (number === 3) return positionColors[4];
                                        return positionColors[0];
                                    };
                                    const color = getColorForNumber(selectedCat.number);
                                    
                                    return (
                                        <>
                                            <div 
                                                className="w-[30px] h-[30px] rounded-full flex items-center justify-center"
                                                style={{ borderColor: color, borderWidth: '1px' }}
                                            >
                                                <span className="text-[14px] font-semibold" style={{ color: color }}>{selectedCat.number}</span>
                                            </div>
                                            <div className='w-[60px] h-[20px] border border-[#353232] rounded-[3px] flex flex-row items-center justify-center'>
                                                <span className='flex items-center gap-1 text-[12px] text-[#D8D8D8]'>
                                                    <SettingsIcon/>
                                                    {selectedCat.name}
                                                    </span>
                                            </div>
                                        </>
                                    );
                                })()
                            ) : (
                                <>
                                    <div 
                                        className="w-[30px] h-[30px] rounded-full flex items-center justify-center"
                                        style={{ borderColor: '#FFB700', borderWidth: '1px' }}
                                    >
                                        <span className="text-[14px] font-semibold" style={{ color: '#FFB700' }}>3</span>
                                    </div>
                                    <div className='w-[60px] h-[20px] border border-[#353232] rounded-[3px] flex flex-row items-center justify-center'>
                                        <span className='text-[12px] text-[#D8D8D8]'>3 Basic</span>
                                    </div>
                                </>
                            )}
                        </div>
                    }
                    content={categoriesContent}
                    disabled={isLimited}
                />

                <PromoTimeFormAccordion
                    number={3}
                    title='Promo time'
                    content={<PromoTime />}
                    child={
                        <div className='flex flex-row items-center gap-1'>
                            <div className='w-[25px] h-[25px] rounded-[2.5px] bg-[#1A1A1A] font-roboto flex flex-row items-center justify-center text-[11px]'>
                                {selectedDay || 3}
                            </div>
                            <span className='text-[#D8D8D8] text-[12px]'>D</span>
                        </div>
                    }
                />

                {/* <FormAccardion
                    number={4}
                    title='AddPromoter'
                    child={
                        <div className={`w-[110px] h-[34px] rounded-[8px] ${isLimited ? 'bg-[#959595]' : 'bg-[#D3D3D3]'} flex flex-row items-center justify-center whitespace-nowrap`}>
                            <span className='text-[16px] text-black font-roboto font-semi'>Complete</span>
                        </div>
                    }
                    disabled={isLimited}
                /> */}

                <TasksFormAccordion
                    number={4}
                    title='Add tasks'
                    child={
                        <div className='w-[44px] h-[26px] rounded-[40px] bg-[#D3D3D3] flex flex-row items-center justify-center whitespace-nowrap'>
                            <span className='text-[13px] text-black font-medium'>{tasksCount}/10</span>
                        </div>
                    }
                    content={<TasksList maxTasks={10} />}
                />
            </div>

            {/* FINISH/UPDATE — inactive until form complete + ≥1 task; tap shows hint snackbar */}
            <div className='w-full flex flex-row items-center justify-center mt-4 mb-4'>
                <div
                    role="button"
                    tabIndex={0}
                    aria-disabled={!canSubmitPromo}
                    onClick={handleFinishClick}
                    className={`rounded-[6px] flex flex-row items-center justify-center w-[244px] h-[45px] font-bold text-[15px] transition-colors duration-200 ${
                        canSubmitPromo
                            ? `cursor-pointer ${
                                isEditMode
                                    ? 'bg-[#F59E0B] hover:bg-[#D97706] text-black'
                                    : 'bg-[#207FE0]/90 hover:bg-[#1a6bc7] text-white'
                            }`
                            : 'bg-[#353232] text-[#959595] cursor-pointer opacity-60'
                    }`}
                >
                    {isEditMode ? 'UPDATE' : 'FINISH'}
                </div>
            </div>

            <Snackbar
                message={snackbar.message}
                type={snackbar.type}
                isVisible={snackbar.isVisible}
                isAnimated={snackbarAnimation}
                tokenData={snackbar.tokenData}
                blockchainName={snackbar.blockchainName}
                isBlockchainSwitched={snackbar.isBlockchainSwitched}
                onClose={closeSnackbar}
            />
            </div>
        </div>
        </PromoGuideProvider>
    );
}
